/** Transaction types for compliance checklist (Progress Tracker + Progress Report). */
export const COMPLIANCE_TRANSACTION_TYPE_OPTIONS = [
  { value: "", label: "— Select transaction type —" },
  { value: "offer-lease", label: "Offer for Lease" },
  { value: "offer-sale", label: "Offer for Sale" },
  { value: "cooperating-lease", label: "Cooperating - Lease" },
  { value: "cooperating-sale", label: "Cooperating - Sale" },
  { value: "pre-construction", label: "Pre - Construction" },
];

const TYPE_ALIASES = {
  "offer for lease": "offer-lease",
  "offer-lease": "offer-lease",
  "offer for sale": "offer-sale",
  "offer-sale": "offer-sale",
  "coperating - lease": "cooperating-lease",
  "cooperating - lease": "cooperating-lease",
  "cooperating-lease": "cooperating-lease",
  "coperating - sale": "cooperating-sale",
  "cooperating - sale": "cooperating-sale",
  "cooperating-sale": "cooperating-sale",
  "pre - construction": "pre-construction",
  "pre-construction": "pre-construction",
};

/** Mandatory + optional preset forms per transaction type. */
export const COMPLIANCE_CHECKLIST_BY_TYPE = {
  "offer-sale": {
    mandatory: [
      { id: "aps", label: "APS" },
      { id: "schedule_a", label: "Schedule A" },
      { id: "schedule_b", label: "Schedule B" },
      {
        id: "confirmation_corporation",
        label: "Confirmation of Corporation",
      },
      { id: "deposit_slip", label: "Deposit slip" },
      { id: "deposit_receipt", label: "Deposit receipt" },
      { id: "buyer_lawyer", label: "Buyer Lawyer information" },
      { id: "seller_lawyer", label: "Seller lawyer information" },
    ],
    optional: [
      { id: "reco_guide", label: "Reco Information Guide" },
      { id: "waiver_nof", label: "Waiver / NOF" },
      { id: "amendment", label: "Amendment" },
      {
        id: "sellers_void_cheque",
        label: "Sellers void cheque and email",
      },
      { id: "mutual_release", label: "Mutual release" },
    ],
  },
  "offer-lease": {
    mandatory: [
      { id: "lease_agreement", label: "Lease agreement" },
      { id: "schedule_a", label: "Schedule A" },
      { id: "schedule_b", label: "Schedule B" },
      {
        id: "confirmation_corporation",
        label: "Confirmation of Corporation",
      },
      { id: "deposit_slip", label: "Deposit slip" },
      { id: "deposit_receipt", label: "Deposit receipt" },
      { id: "ota", label: "OTA" },
    ],
    optional: [
      { id: "reco_guide", label: "Reco Information Guide" },
      { id: "waiver_nof", label: "Waiver / NOF" },
      { id: "amendment", label: "Amendment" },
      { id: "rental_application", label: "Rental application" },
      { id: "landlord_void_cheque", label: "Landlord void cheque" },
    ],
  },
  "cooperating-sale": {
    mandatory: [
      { id: "reco_guide", label: "Reco Information Guide" },
      { id: "form_801", label: "Form 801" },
      { id: "fintrac", label: "FINTRAC" },
      {
        id: "buyer_rep_agreement",
        label: "Buyer representation agreement",
      },
      { id: "aps", label: "APS" },
      { id: "schedule_a", label: "Schedule A" },
      { id: "schedule_b", label: "Schedule B" },
      {
        id: "confirmation_corporation",
        label: "Confirmation of Corporation",
      },
      { id: "deposit_slip", label: "Deposit slip" },
      { id: "deposit_receipt", label: "Deposit receipt" },
      { id: "buyer_lawyer", label: "Buyer Lawyer information" },
      { id: "seller_lawyer", label: "Seller lawyer information" },
    ],
    optional: [
      { id: "waiver_nof", label: "Waiver / NOF" },
      { id: "amendment", label: "Amendment" },
      { id: "mutual_release", label: "Mutual release" },
    ],
  },
  "cooperating-lease": {
    mandatory: [
      { id: "reco_guide", label: "Reco Information Guide" },
      { id: "lease_agreement", label: "Lease agreement" },
      { id: "schedule_a", label: "Schedule A" },
      { id: "schedule_b", label: "Schedule B" },
      {
        id: "confirmation_corporation",
        label: "Confirmation of Corporation",
      },
      { id: "deposit_slip", label: "Deposit slip" },
      { id: "deposit_receipt", label: "Deposit receipt" },
      { id: "ota", label: "OTA" },
    ],
    optional: [
      { id: "waiver_nof", label: "Waiver / NOF" },
      { id: "amendment", label: "Amendment" },
      { id: "rental_application", label: "Rental application" },
    ],
  },
  "pre-construction": {
    mandatory: [
      {
        id: "referral_builder",
        label: "Referral agreement with builder",
      },
      { id: "aps", label: "APS" },
    ],
    optional: [
      {
        id: "referral_brokerage",
        label: "Referral agreement with other brokerage",
      },
      { id: "tcs", label: "TCS" },
      { id: "reco_guide", label: "RECO Information Guide" },
      { id: "fintrac", label: "FINTRAC" },
    ],
  },
};

/** Map stored compliance to Yes / No / Pending (legacy Complete / Outstanding supported). */
export function normalizeComplianceValue(value) {
  const v = String(value ?? "").trim();
  if (v === "Yes" || v === "No" || v === "Pending") return v;
  if (v === "Complete") return "Yes";
  if (v === "Outstanding") return "Pending";
  return "No";
}

export function normalizeTransactionType(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const key = raw.toLowerCase();
  if (TYPE_ALIASES[key]) return TYPE_ALIASES[key];
  if (COMPLIANCE_CHECKLIST_BY_TYPE[raw]) return raw;
  return "";
}

function presetRowsForType(transactionType, removedPresetIds = []) {
  const type = normalizeTransactionType(transactionType);
  const config = COMPLIANCE_CHECKLIST_BY_TYPE[type];
  if (!config) return [];
  const removed = new Set((removedPresetIds || []).map(String));

  const mandatory = (config.mandatory || []).map((preset) => ({
    id: preset.id,
    label: preset.label,
    checked: false,
    preset: true,
    removable: false,
    group: "mandatory",
  }));

  const optional = (config.optional || [])
    .filter((preset) => !removed.has(preset.id))
    .map((preset) => ({
      id: preset.id,
      label: preset.label,
      checked: false,
      preset: true,
      removable: true,
      group: "optional",
    }));

  return [...mandatory, ...optional];
}

function allPresetIdsForType(transactionType) {
  const type = normalizeTransactionType(transactionType);
  const config = COMPLIANCE_CHECKLIST_BY_TYPE[type];
  if (!config) return new Set();
  return new Set([
    ...(config.mandatory || []).map((i) => i.id),
    ...(config.optional || []).map((i) => i.id),
  ]);
}

/**
 * Merge saved checklist with presets for the selected transaction type.
 * Custom rows added by the user are preserved.
 */
export function normalizeComplianceChecklist(
  raw,
  transactionType,
  removedPresetIds = [],
) {
  const type = normalizeTransactionType(transactionType);
  const presets = presetRowsForType(type, removedPresetIds);
  const saved = Array.isArray(raw) ? raw : [];
  const byId = new Map(
    saved.filter((i) => i && i.id).map((i) => [String(i.id), i]),
  );
  const validIds = allPresetIdsForType(type);

  const items = presets
    .filter((preset) => validIds.has(preset.id))
    .map((preset) => ({
      ...preset,
      checked: !!byId.get(preset.id)?.checked,
    }));

  for (const item of saved) {
    if (!item?.id || validIds.has(String(item.id))) continue;
    const label = String(item.label || "").trim();
    if (!label) continue;
    items.push({
      id: String(item.id),
      label,
      checked: !!item.checked,
      preset: false,
      removable: true,
      group: "custom",
    });
  }

  return items;
}

/** Whether a checklist row can be removed from the UI. */
export function canRemoveComplianceItem(item) {
  if (!item) return false;
  if (!item.preset) return true;
  return !!item.removable;
}

/** Derive tracker dropdown + progress report compliance from checklist ticks. */
export function complianceStatusFromChecklist(items) {
  const list = items || [];
  if (list.length === 0) {
    return { complianceComplete: "No", complianceReport: "No" };
  }
  const allChecked = list.every((i) => i.checked);
  const anyChecked = list.some((i) => i.checked);
  if (allChecked) {
    return { complianceComplete: "Yes", complianceReport: "Yes" };
  }
  if (anyChecked) {
    return { complianceComplete: "Pending", complianceReport: "Pending" };
  }
  return { complianceComplete: "No", complianceReport: "No" };
}

export function complianceReportFromTrackerFields(pt) {
  const type = normalizeTransactionType(pt?.complianceTransactionType);
  const checklist = normalizeComplianceChecklist(
    pt?.complianceChecklist,
    type,
    pt?.complianceRemovedPresetIds,
  );
  if (checklist.length > 0 && checklist.every((i) => i.checked)) {
    return "Yes";
  }
  const synced = complianceStatusFromChecklist(checklist);
  const v = normalizeComplianceValue(
    pt?.complianceReport ?? pt?.complianceComplete ?? synced.complianceReport,
  );
  return v || synced.complianceReport;
}

export function newCustomComplianceItemId() {
  return `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
